/**
 * V70G8C — 팝업 표시 보류 (공지 상세를 보는 동안 다음 팝업이 상세 화면 위에 뜨지 않도록).
 * 상세 화면/상세 모달이 마운트되어 있는 동안 hold 를 잡고, 닫히면 놓는다.
 * 보류 중에는 대기열을 그대로 두고 표시만 멈춘다 (팝업을 닫거나 기록하지 않음).
 *
 * V70G8E — 상세 인계 보류 (pending detail hold).
 *   '상세 보기' 클릭 / 상세 딥링크 진입 시점부터 **그 상세가 실제로 열려 보류를 넘겨받을 때까지** 보류를 유지한다.
 *   (게임대회 대시보드는 접근 권한 조회 가드 → 페이지 마운트 → URL 파라미터 처리 → 상세 모달 마운트 순서라
 *    주소가 바뀐 시점과 상세가 열린 시점 사이에 공백이 있다. 이 공백에 다음 팝업이 뜨거나 shown 기록되면 안 된다.)
 *   해제 조건: 같은 키의 상세가 열림(인계) / 다른 화면으로 이동 / 안전 타임아웃.
 */
import { useEffect, useState } from 'react';

const holders = new Set<symbol>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

export function acquirePopupHold(): () => void {
  const token = Symbol('popup-hold');
  holders.add(token);
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders.delete(token);
    emit();
  };
}

// ───────────── V70G8E: 상세 인계 보류 ─────────────

/** 상세가 끝내 열리지 않을 때(권한 없음·네트워크 오류 등) 대기열이 멈추지 않도록 하는 안전 시간 */
export const DETAIL_HOLD_TIMEOUT_MS = 15_000;

/** 현재 열려 있는 상세 (키 → 마운트 수) */
const openDetails = new Map<string, number>();
let pendingDetail: {
  key: string;
  pathname: string;
  release: () => void;
  timer: ReturnType<typeof setTimeout>;
} | null = null;

/** 상세 키 (소스:공지ID) — 팝업과 상세 화면이 같은 규칙으로 만든다 */
export function popupDetailKey(source: 'erp' | 'gameCenter', announcementId: string): string {
  return `${source}:${announcementId}`;
}

export function isDetailOpen(key: string): boolean {
  return (openDetails.get(key) || 0) > 0;
}

export function pendingDetailHold(): { key: string; pathname: string } | null {
  return pendingDetail ? { key: pendingDetail.key, pathname: pendingDetail.pathname } : null;
}

export function cancelPendingDetailHold(): void {
  const p = pendingDetail;
  if (!p) return;
  pendingDetail = null;
  clearTimeout(p.timer);
  p.release();
}

/**
 * 상세가 열릴 때까지 팝업 보류. 이미 그 상세가 열려 있거나 같은 키로 대기 중이면 아무것도 하지 않는다.
 * @param pathname 상세가 열릴 화면 경로 (다른 경로로 이동하면 보류 해제)
 */
export function holdUntilDetailOpens(key: string, pathname: string, timeoutMs = DETAIL_HOLD_TIMEOUT_MS): void {
  if (isDetailOpen(key)) return;
  if (pendingDetail && pendingDetail.key === key) return;
  cancelPendingDetailHold();
  const release = acquirePopupHold();
  pendingDetail = { key, pathname, release, timer: setTimeout(cancelPendingDetailHold, timeoutMs) };
}

/** 테스트 전용: 남은 보류 정리 */
export function resetPopupHoldsForTest(): void {
  if (pendingDetail) clearTimeout(pendingDetail.timer);
  pendingDetail = null;
  openDetails.clear();
  holders.clear();
  emit();
}

export function isPopupHeld(): boolean {
  return holders.size > 0;
}

export function subscribePopupHold(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 현재 보류 여부 구독 */
export function usePopupHeld(): boolean {
  const [held, setHeld] = useState<boolean>(isPopupHeld());
  useEffect(() => {
    const update = () => setHeld(isPopupHeld());
    update();
    return subscribePopupHold(update);
  }, []);
  return held;
}

/**
 * 마운트되어 있는 동안 (active=true 일 때) 팝업 표시 보류.
 * V70G8E: detailKey 를 주면 같은 키의 '상세 인계 보류'를 넘겨받는다 (보류를 먼저 잡고 대기 보류를 놓아 공백 없음).
 */
export function useHoldPopups(active = true, detailKey?: string | null): void {
  useEffect(() => {
    if (!active) return undefined;
    const release = acquirePopupHold();
    if (detailKey) {
      openDetails.set(detailKey, (openDetails.get(detailKey) || 0) + 1);
      if (pendingDetail && pendingDetail.key === detailKey) cancelPendingDetailHold();
    }
    return () => {
      if (detailKey) {
        const n = (openDetails.get(detailKey) || 0) - 1;
        if (n > 0) openDetails.set(detailKey, n);
        else openDetails.delete(detailKey);
      }
      release();
    };
  }, [active, detailKey]);
}
